import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { getLlmKeys, setLlmKey, deleteLlmKey, testLlmKey } from '../services/api';
import { toast } from 'sonner';
import { KeyRound, Loader2, Save, Trash2, CheckCircle2, ShieldAlert } from 'lucide-react';

const PROVIDER_LABELS = {
  anthropic: 'Anthropic (Claude)',
  google: 'Google (Gemini)',
  openai: 'OpenAI',
  emergent: 'Emergent',
};

const sourceBadge = (source) => {
  if (source === 'db') return { label: 'Stored in vault', className: 'bg-green-50 text-green-700 border-green-200' };
  if (source === 'env') return { label: 'From environment', className: 'bg-slate-50 text-slate-600 border-slate-200' };
  return { label: 'Not configured', className: 'bg-amber-50 text-amber-700 border-amber-200' };
};

const LlmKeysPanel = () => {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inputs, setInputs] = useState({});      // provider -> pending key text
  const [busy, setBusy] = useState({});          // provider -> 'save'|'clear'|'test'

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getLlmKeys();
      setProviders(res.data.providers || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load API keys');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setBusyFor = (provider, action) => setBusy((b) => ({ ...b, [provider]: action }));
  const clearBusy = (provider) => setBusy((b) => ({ ...b, [provider]: null }));

  const handleSave = async (provider) => {
    const key = (inputs[provider] || '').trim();
    if (!key) { toast.error('Enter a key first'); return; }
    setBusyFor(provider, 'save');
    try {
      await setLlmKey(provider, key);
      setInputs((s) => ({ ...s, [provider]: '' }));
      toast.success(`${PROVIDER_LABELS[provider] || provider} key saved`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save key');
    } finally {
      clearBusy(provider);
    }
  };

  const handleClear = async (provider) => {
    setBusyFor(provider, 'clear');
    try {
      await deleteLlmKey(provider);
      toast.success(`${PROVIDER_LABELS[provider] || provider} key cleared`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to clear key');
    } finally {
      clearBusy(provider);
    }
  };

  const handleTest = async (provider) => {
    setBusyFor(provider, 'test');
    try {
      const res = await testLlmKey(provider);
      if (res.data.ok) toast.success(`${PROVIDER_LABELS[provider] || provider}: key works`);
      else toast.error(`${PROVIDER_LABELS[provider] || provider}: ${res.data.message || 'test failed'}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Test failed');
    } finally {
      clearBusy(provider);
    }
  };

  return (
    <Card className="border-slate-200" data-testid="llm-keys-panel">
      <CardHeader>
        <CardTitle className="text-lg font-semibold font-['Plus_Jakarta_Sans'] flex items-center gap-2">
          <KeyRound className="h-5 w-5 text-slate-500" />
          LLM Provider API Keys
        </CardTitle>
        <CardDescription>
          Keys are encrypted at rest and shared across the app. A stored key overrides the
          environment variable. Keys are write-only — they are never shown again after saving.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="space-y-4" data-testid="llm-keys-list">
            {providers.map((p) => {
              const badge = sourceBadge(p.source);
              const isBusy = !!busy[p.provider];
              return (
                <div key={p.provider} className="border border-slate-200 rounded-lg p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-slate-800">
                        {PROVIDER_LABELS[p.provider] || p.provider}
                      </span>
                      <span className={`inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full border ${badge.className}`}>
                        {badge.label}
                      </span>
                      {p.source === 'db' && p.key_last4 && (
                        <span className="text-xs text-slate-400 font-mono">••••{p.key_last4}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline" size="sm"
                        onClick={() => handleTest(p.provider)}
                        disabled={isBusy || !p.configured}
                        data-testid={`test-key-${p.provider}`}
                      >
                        {busy[p.provider] === 'test'
                          ? <Loader2 className="h-4 w-4 animate-spin" />
                          : <><CheckCircle2 className="h-4 w-4 mr-1" /> Test</>}
                      </Button>
                      {p.source === 'db' && (
                        <Button
                          variant="outline" size="sm"
                          className="text-red-600 border-red-200 hover:bg-red-50"
                          onClick={() => handleClear(p.provider)}
                          disabled={isBusy}
                          data-testid={`clear-key-${p.provider}`}
                        >
                          {busy[p.provider] === 'clear'
                            ? <Loader2 className="h-4 w-4 animate-spin" />
                            : <><Trash2 className="h-4 w-4 mr-1" /> Clear</>}
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      type="password"
                      autoComplete="new-password"
                      placeholder={p.source === 'db' ? 'Enter a new key to replace…' : 'Paste API key…'}
                      value={inputs[p.provider] || ''}
                      onChange={(e) => setInputs((s) => ({ ...s, [p.provider]: e.target.value }))}
                      data-testid={`key-input-${p.provider}`}
                    />
                    <Button
                      onClick={() => handleSave(p.provider)}
                      disabled={isBusy || !(inputs[p.provider] || '').trim()}
                      className="bg-[#2B8FD8] hover:bg-[#2075b3] shrink-0"
                      data-testid={`save-key-${p.provider}`}
                    >
                      {busy[p.provider] === 'save'
                        ? <Loader2 className="h-4 w-4 animate-spin" />
                        : <><Save className="h-4 w-4 mr-1" /> Save</>}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="flex items-start gap-3 mt-5 p-3 rounded-lg bg-amber-50/60 border border-amber-100">
          <ShieldAlert className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
          <p className="text-xs text-amber-700">
            The master encryption key and JWT secret still live in the server environment by
            design. Storing provider keys here removes them from source files and lets you rotate
            without a redeploy.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default LlmKeysPanel;
